// Reusable button component with Velumio styling
import React from 'react'

interface ButtonProps {
  children: React.ReactNode
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
  className?: string
}

export default function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  className = '',
}: ButtonProps) {

  const baseStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'var(--font-body)',
    fontWeight: 500,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.6 : 1,
    transition: 'all var(--transition-base)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    width: fullWidth ? '100%' : 'auto',
    whiteSpace: 'nowrap',
  }

  const sizeStyles: Record<string, React.CSSProperties> = {
    sm: { fontSize: '13px', padding: '8px 16px', height: '36px' },
    md: { fontSize: '15px', padding: '12px 24px', height: '46px' },
    lg: { fontSize: '17px', padding: '16px 32px', height: '56px' },
  }

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      background: 'var(--navy)',
      color: 'var(--cream)',
    },
    secondary: {
      background: 'transparent',
      color: 'var(--ink)',
      border: '1.5px solid var(--warm-border)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--muted)',
    },
    danger: {
      background: 'var(--rose)',
      color: 'var(--white)',
    },
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={className}
      style={{
        ...baseStyles,
        ...sizeStyles[size],
        ...variantStyles[variant],
      }}
    >
      {loading ? (
        <span style={{
          width: '16px',
          height: '16px',
          border: '2px solid currentColor',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          display: 'inline-block',
        }} />
      ) : children}
    </button>
  )
}